import React, { useState } from 'react';
import { Button } from './button';
import { ConfirmationDialog } from './confirmation-dialog';

type EventButtonProps = {
  /** The event as the button says it: "Privileges Granted", "Approve Shifts". */
  label: string;
  /** Button text while the event is being logged; defaults to `<label>...`. */
  pendingLabel?: string;
  /** What logging the event does — the one line the confirm dialog shows. */
  consequence: React.ReactNode;
  /** Logs the event; the dialog stays open (busy) until it settles. */
  onConfirm: () => Promise<unknown> | void;
  isPending?: boolean;
  disabled?: boolean;
  small?: boolean;
  'data-testid'?: string;
};

/**
 * A drawer's EVENT button ("Privileges Granted", "PCF Enrolled", "Approve Shifts"):
 * one click used to log the event straight away, and the workflow rules behind an
 * event cascade into status changes that nothing un-happens. So the button asks first
 * — the event's name as the question, one line on what it does, and the event's name
 * again on the confirm — unlike status changes, which already go through the pick-then-
 * save status modal.
 */
export function EventButton({
  label,
  pendingLabel,
  consequence,
  onConfirm,
  isPending = false,
  disabled = false,
  small = false,
  'data-testid': testId,
}: EventButtonProps) {
  const [isConfirming, setIsConfirming] = useState(false);

  const confirm = async () => {
    try {
      await onConfirm();
    } finally {
      // the caller toasts success / failure; either way the question is answered
      setIsConfirming(false);
    }
  };

  return (
    <>
      <Button
        color="brandgreen0"
        onClick={() => setIsConfirming(true)}
        disabled={disabled || isPending}
        small={small}
        data-testid={testId}
      >
        {isPending ? (pendingLabel ?? `${label}...`) : label}
      </Button>
      <ConfirmationDialog
        open={isConfirming}
        onClose={() => {
          if (!isPending) setIsConfirming(false);
        }}
        onConfirm={confirm}
        title={`${label}?`}
        body={consequence}
        confirmText={label}
        confirmVariant="primary"
        isLoading={isPending}
      />
    </>
  );
}
